import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import AdminPanel from './AdminPanel';
import CalendarView from './RateAvailability';
import PromotionForm from './PromotionForm';
import RoomManager from './RoomManager';
import HotelInfoManagerV2 from './pages/HotelInfoManagerV2';
import SpaManager from './components/SpaManager/SpaManager';
import PropertyPanel from './PropertyPanel/PropertyPanel';
import LandingPage from './LandingPage';
import SettingsPage from './SettingsPage';
import ReservationCenter from './ReservationCenter';
import GuestCenter from './pages/GuestCenter';
import ConciergeCenter from './pages/ConciergeCenter';
import ReservationDetailPage from './pages/ReservationDetailPage';
import ExplorePreviewPage from './pages/ExplorePreviewPage';
import { LoginLogTable } from './components/LoginLogTable';
import RestaurantManager from './pages/RestaurantManager';
import './App.css';
import { Box, Menu, MenuItem, Avatar, Button } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Routes, Route, useLocation, Link, useNavigate } from 'react-router-dom';
import {
  Home,
  CalendarDays,
  Tag,
  BedDouble,
  Building,
  Settings,
  FileText,
  ClipboardList,
  Download,
  LogOut,
  MessagesSquare
} from 'lucide-react';

import { useConciergeStore } from './store/conciergeStore';

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [propertyName, setPropertyName] = useState('');
  const [user, setUser] = useState(null);
  const [allowedFeatures, setAllowedFeatures] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [newBookingCount, setNewBookingCount] = useState(0);
  const [newGuestCount, setNewGuestCount] = useState(0);
  const [newTourBookingCount, setNewTourBookingCount] = useState(0);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installReady, setInstallReady] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user?.properties?.[0]?.role === 'admin';
  const allowedTabs = isAdmin
    ? ['admin', 'calendar', 'promotion', 'room', 'hotelInfo', 'property', 'settings', 'reservation', 'guest', 'concierge']
    : JSON.parse(localStorage.getItem('allowedTabs') || '[]');
  const { threads: conciergeThreads, fetchThreads } = useConciergeStore();
  const conciergeUnreadCount = useMemo(() => {
    try {
      return (conciergeThreads || []).reduce((sum, t) => sum + (t?.unread?.staff || 0), 0);
    } catch {
      return 0;
    }
  }, [conciergeThreads]);
  const resolvedPropertyId = useMemo(() => (
    user?.property?._id ||
    user?.properties?.[0]?.propertyId?._id ||
    user?.properties?.[0]?.propertyId ||
    null
  ), [user]);
  // Load role-based allowedFeatures and cache them
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const role = user?.properties?.[0]?.role || user?.role;
        const propertyId = resolvedPropertyId;
        if (!role || !propertyId) return;
        const url = `${process.env.REACT_APP_API_URL}/api/role-permissions/${role}?propertyId=${propertyId}`;
        const res = await fetch(url);
        if (!res.ok) {
          console.warn('Failed to fetch role-permissions:', res.status, url);
          return;
        }
        const data = await res.json();
        const feats = data?.allowedFeatures || {};
        setAllowedFeatures(feats);
        try {
          localStorage.setItem('allowedFeatures', JSON.stringify(feats));
        } catch (e) {
          console.warn('Could not cache allowedFeatures in localStorage:', e);
        }
        console.log('✅ Loaded allowedFeatures:', feats);
      } catch (e) {
        console.warn('loadPermissions error', e);
      }
    };
    if (user) loadPermissions();
  }, [user, resolvedPropertyId]);
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setInstallReady(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallApp = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then(() => {
        setDeferredPrompt(null);
        setInstallReady(false);
      });
    }
  };
  useEffect(() => {
    const fetchNewBookingCount = async () => {
      const propertyId = resolvedPropertyId;
      if (!propertyId) return;

      // Debug: log API base URL
      console.log("🌐 API BASE URL:", process.env.REACT_APP_API_URL);
      // Expose map keys at runtime for ConciergeBubble MapAttachment (CRA env + runtime override)
      if (typeof window !== 'undefined') {
        if (!window.__GOOGLE_STATIC_MAPS_KEY) {
          window.__GOOGLE_STATIC_MAPS_KEY = (process.env && process.env.REACT_APP_GOOGLE_STATIC_MAPS_KEY) || window.__GOOGLE_STATIC_MAPS_KEY || '';
        }
        if (!window.__MAPBOX_TOKEN) {
          window.__MAPBOX_TOKEN = (process.env && process.env.REACT_APP_MAPBOX_TOKEN) || window.__MAPBOX_TOKEN || '';
        }
        try {
          console.log('[App] Map keys present?', {
            google: !!window.__GOOGLE_STATIC_MAPS_KEY,
            mapbox: !!window.__MAPBOX_TOKEN,
            gHead: window.__GOOGLE_STATIC_MAPS_KEY ? String(window.__GOOGLE_STATIC_MAPS_KEY).slice(0, 6) + '…' : ''
          });
        } catch {}
      }

      // The backend now uses `isUnseen` to track new bookings.
      // The frontend fetch logic remains the same; the backend returns the correct count.
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/new-count?propertyId=${propertyId}`);
        const data = await res.json();
        if (data.success) setNewBookingCount(data.count);
      } catch (error) {
        console.error("Failed to fetch new booking count:", error);
      }
    };

    const fetchNewGuestCount = async () => {
      const propertyId = resolvedPropertyId;
      if (!propertyId) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/new-count?propertyId=${propertyId}`);
        const data = await res.json();
        // Debug log for new guest badge logic
        console.log("🔔 New Guest Count:", data.count);
        if (data.success) setNewGuestCount(data.count);
      } catch (error) {
        console.error("Failed to fetch new guest count:", error);
      }
    };

    if (user) {
      fetchNewBookingCount();
      fetchNewGuestCount();
      const interval1 = setInterval(fetchNewBookingCount, 30000);
      const interval3 = setInterval(fetchNewGuestCount, 30000);
      return () => {
        clearInterval(interval1);
        clearInterval(interval3);
      };
    }
  }, [user, resolvedPropertyId]);

  // Reset newTourBookingCount and mark as seen when navigating to /tourbooking
  useEffect(() => {
    if (location.pathname === '/tourbooking') {
      setNewTourBookingCount(0);
      const propertyId = resolvedPropertyId;
      if (propertyId) {
        fetch(`${process.env.REACT_APP_API_URL}/api/tour-booking/mark-seen?propertyId=${propertyId}`, {
          method: 'PUT'
        }).catch(err => console.error('❌ Failed to mark tour bookings as seen:', err));
      }
    }
  }, [location.pathname, resolvedPropertyId, setNewTourBookingCount]);

  // Reset newBookingCount and mark as seen when navigating to /reservation
  useEffect(() => {
    if (location.pathname === '/reservation') {
      setNewBookingCount(0);
      const propertyId = resolvedPropertyId;
      if (propertyId) {
        fetch(`${process.env.REACT_APP_API_URL}/api/bookings/mark-seen?propertyId=${propertyId}`, {
          method: 'PUT'
        }).catch(err => console.error('❌ Failed to mark room bookings as seen:', err));
      }
    }
  }, [location.pathname, resolvedPropertyId, setNewBookingCount]);

  // Reset newGuestCount and mark as seen when navigating to /guest
  useEffect(() => {
    if (location.pathname === '/guest') {
      setNewGuestCount(0);
      const propertyId = user?.property?._id || user?.properties?.[0]?.propertyId?._id;
      if (propertyId) {
        fetch(`${process.env.REACT_APP_API_URL}/api/user/mark-seen?propertyId=${propertyId}`, {
          method: 'PUT'
        }).catch(err => console.error('❌ Failed to mark guest profiles as seen:', err));
      }
    }
  }, [location.pathname, user, setNewGuestCount]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const token = localStorage.getItem('token');

    if (storedUser && token) {
      setUser(storedUser);
      setAuthenticated(true);
      setPropertyName(storedUser.property?.name || '');
    }
  }, []);

  useEffect(() => {
    if (!resolvedPropertyId || propertyName) return;
    let ignore = false;
    const controller = new AbortController();
    const fetchPropertyName = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/property/${resolvedPropertyId}`, {
          signal: controller.signal,
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!ignore && data?.name) {
          setPropertyName(data.name);
        }
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.warn('[App] failed to load property details', err);
      }
    };
    fetchPropertyName();
    return () => {
      ignore = true;
      controller.abort();
    };
  }, [resolvedPropertyId, propertyName, setPropertyName]);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(stored);
    if (stored.property?.name) {
      setPropertyName(stored.property.name);
    }
    setAnchorEl(null); // ✅ close menu after login
  }, [authenticated]);

  // Expose current staff user for chat attribution and hydrate concierge store
  useEffect(() => {
    try {
      if (user) {
        if (typeof window !== 'undefined') {
          window.__EF_USER = user; // used by ConciergeComposer as a fallback
        }
        try {
          const api = useConciergeStore.getState && useConciergeStore.getState();
          if (api && typeof api.setMe === 'function') {
            api.setMe(user);
          }
        } catch (e) {
          console.debug('[App] setMe not available in concierge store', e);
        }
        console.debug('[App] exposed staff user for chat', { id: user?._id, role: user?.role || user?.properties?.[0]?.role });
      } else {
        if (typeof window !== 'undefined' && window.__EF_USER) {
          try { delete window.__EF_USER; } catch {}
        }
      }
    } catch (e) {
      console.warn('[App] expose staff user failed', e);
    }
  }, [user]);

  useEffect(() => {
    const propertyId = user?.property?._id || user?.properties?.[0]?.propertyId?._id;
    if (!propertyId) return;
    const load = () => fetchThreads(propertyId, { page: 1, limit: 50 }).catch(err => console.warn('concierge fetchThreads error', err));
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [user, fetchThreads]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('allowedTabs');
    setAuthenticated(false);
    console.log('✅ LocalStorage after logout:', {
      token: localStorage.getItem('token'),
      refreshToken: localStorage.getItem('refreshToken'),
      user: localStorage.getItem('user'),
      allowedTabs: localStorage.getItem('allowedTabs')
    });
    window.location.reload();
  }
  const resolvedProperty = user?.property || user?.properties?.[0]?.propertyId;
  const currentLogo = resolvedProperty?.logo;
  const currentPropertyName = resolvedProperty?.name || 'No property name';

  const getButtonStyles = (path) => ({
    color: location.pathname === path ? '#000' : '#666',
    fontWeight: location.pathname === path ? 'bold' : 'normal',
    backgroundColor: location.pathname === path ? '#e8f0fe' : 'transparent',
    borderRadius: 6,
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  });

  // SkeletonBlock reusable loading component
  const SkeletonBlock = ({ className }) => (
    <motion.div
      className={`bg-gray-200 rounded ${className}`}
      initial={{ backgroundPosition: '100% 0' }}
      animate={{ backgroundPosition: '-100% 0' }}
      transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
      style={{
        backgroundImage: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
        backgroundSize: '200% 100%',
      }}
    />
  );

  const tabButtonSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 80,
    padding: '8px 4px',
    textAlign: 'center',
  };

  const tabLabelStyle = {
    fontSize: '0.75rem',
    marginTop: 4,
  };

  return (
    <div>
      {!authenticated && <LandingPage onLoginSuccess={() => { setAuthenticated(true); }} />}
      {authenticated && (
        <>
          <Box
            sx={{
              display: 'flex',
              gap: 4,
              padding: '12px 24px',
              backgroundColor: '#ffffff',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            {allowedTabs.includes('admin') && (
              <Button
                component={Link}
                to="/admin"
                variant="text"
                sx={{
                  ...getButtonStyles('/admin'),
                  ...tabButtonSx,
                }}
              >
                <ClipboardList size={22} />
                <span style={tabLabelStyle}>Admin Panel</span>
              </Button>
            )}
            {allowedTabs.includes('calendar') && (
              <Button
                component={Link}
                to="/calendar"
                variant="text"
                sx={{
                  ...getButtonStyles('/calendar'),
                  ...tabButtonSx,
                }}
              >
                <CalendarDays size={22} />
                <span style={tabLabelStyle}>Rates & Availability</span>
              </Button>
            )}
            {allowedTabs.includes('promotion') && (
              <Button
                component={Link}
                to="/promotion"
                variant="text"
                sx={{
                  ...getButtonStyles('/promotion'),
                  ...tabButtonSx,
                }}
              >
                <Tag size={22} />
                <span style={tabLabelStyle}>Promotion</span>
              </Button>
            )}
            {allowedTabs.includes('reservation') && (
              <Button
                component={Link}
                to="/reservation"
                variant="text"
                sx={{
                  ...getButtonStyles('/reservation'),
                  ...tabButtonSx,
                }}
              >
                <span style={{ position: 'relative' }}>
                  <FileText size={22} />
                  {newBookingCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: -6,
                      right: -10,
                      backgroundColor: '#ff3b30',
                      color: 'white',
                      borderRadius: '50%',
                      width: 18,
                      height: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      {newBookingCount}
                    </span>
                  )}
                </span>
                <span style={tabLabelStyle}>Reservation</span>
              </Button>
            )}
            {allowedTabs.includes('tourbooking') && (
              <Button
                component={Link}
                to="/tourbooking"
                variant="text"
                sx={{
                  ...getButtonStyles('/tourbooking'),
                  ...tabButtonSx,
                }}
              >
                <span style={{ position: 'relative' }}>
                  <FileText size={22} />
                  {newTourBookingCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: -6,
                      right: -10,
                      backgroundColor: '#ff3b30',
                      color: 'white',
                      borderRadius: '50%',
                      width: 18,
                      height: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      {newTourBookingCount}
                    </span>
                  )}
                </span>
                <span style={tabLabelStyle}>Tour Booking</span>
              </Button>
            )}
            {allowedTabs.includes('room') && (
              <Button
                component={Link}
                to="/room"
                variant="text"
                sx={{
                  ...getButtonStyles('/room'),
                  ...tabButtonSx,
                }}
              >
                <BedDouble size={22} />
                <span style={tabLabelStyle}>Room Manager</span>
              </Button>
            )}
            {allowedTabs.includes('hotelInfo') && (
              <Button
                component={Link}
                to="/hotelInfo"
                variant="text"
                sx={{
                  ...getButtonStyles('/hotelInfo'),
                  ...tabButtonSx,
                }}
              >
                <Building size={22} />
                <span style={tabLabelStyle}>Hotel Manager</span>
              </Button>
            )}
            {allowedTabs.includes('property') && (
              <Button
                component={Link}
                to="/property"
                variant="text"
                sx={{
                  ...getButtonStyles('/property'),
                  ...tabButtonSx,
                }}
              >
                <Home size={22} />
                <span style={tabLabelStyle}>Property</span>
              </Button>
            )}
            {allowedTabs.includes('guest') && (
              <Button
                component={Link}
                to="/guest"
                variant="text"
                sx={{
                  ...getButtonStyles('/guest'),
                  ...tabButtonSx,
                }}
              >
                <span style={{ position: 'relative' }}>
                  <ClipboardList size={22} />
                  {newGuestCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: -6,
                      right: -10,
                      backgroundColor: '#ff3b30',
                      color: 'white',
                      borderRadius: '50%',
                      width: 18,
                      height: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      {newGuestCount}
                    </span>
                  )}
                </span>
                <span style={tabLabelStyle}>Guest Info</span>
              </Button>
            )}
            {allowedTabs.includes('concierge') && (
              <Button
                component={Link}
                to="/inbox"
                variant="text"
                sx={{
                  ...getButtonStyles('/inbox'),
                  ...tabButtonSx,
                }}
              >
                <span style={{ position: 'relative' }}>
                  <MessagesSquare size={22} />
                  {conciergeUnreadCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: -6,
                      right: -10,
                      backgroundColor: '#ff3b30',
                      color: 'white',
                      borderRadius: '50%',
                      width: 18,
                      height: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      {conciergeUnreadCount}
                    </span>
                  )}
                </span>
                <span style={tabLabelStyle}>Inbox</span>
              </Button>
            )}
            
            <Box
              onClick={event => setAnchorEl(event.currentTarget)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                marginLeft: 'auto',
                px: 2,
                py: 1,
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              {currentLogo && (
                <Avatar
                  src={currentLogo.startsWith('http') ? currentLogo : `${process.env.REACT_APP_API_URL}${currentLogo}`}
                  alt="Logo"
                  sx={{ width: 36, height: 36 }}
                />
              )}
              {console.log("🧾 Debug User Info:", user)}
              <Box>
                <Box sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                  {currentPropertyName}
                </Box>
                <Box sx={{ fontSize: '0.75rem', color: '#777' }}>
                  {user?.username || 'User'}
                </Box>
              </Box>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              PaperProps={{
                sx: {
                  minWidth: 180,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  padding: 1,
                },
              }}
            >
              {allowedTabs.includes('settings') && (
                <MenuItem onClick={() => {
                  setAnchorEl(null);
                  navigate('/settings');
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Settings size={16} />
                    Settings
                  </span>
                </MenuItem>
              )}
              {installReady && (
                <MenuItem onClick={handleInstallApp}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Download size={16} />
                    Install App
                  </span>
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <LogOut size={16} />
                  Logout
                </span>
              </MenuItem>
            </Menu>
          </Box>

          <main style={{ marginTop: location.pathname === '/inbox' ? 0 : '20px' }}>
            <Routes>
              {allowedTabs.includes('admin') && (
                <Route path="/admin" element={<AdminPanel />} />
              )}
              {allowedTabs.includes('calendar') && (
                <Route path="/calendar" element={<CalendarView />} />
              )}
              {allowedTabs.includes('promotion') && (
                <Route path="/promotion" element={<PromotionForm />} />
              )}
              {allowedTabs.includes('room') && (
                <Route path="/room" element={<RoomManager />} />
              )}
              {allowedTabs.includes('hotelInfo') && (
                <Route
                  path="/hotelInfo"
                  element={
                    <HotelInfoManagerV2
                      propertyId={resolvedPropertyId}
                      propertyName={user?.property?.name || propertyName}
                      allowedFeatures={allowedFeatures}
                    />
                  }
                />
              )}
              <Route
                path="/hotel-info-v2"
                element={
                  <HotelInfoManagerV2
                    propertyId={resolvedPropertyId}
                    propertyName={user?.property?.name || propertyName}
                    allowedFeatures={allowedFeatures}
                  />
                }
              />
              {allowedTabs.includes('property') && (
                <Route path="/property" element={<PropertyPanel />} />
              )}
              {allowedTabs.includes('reservation') && (
                <Route path="/reservation" element={<ReservationCenter />} />
              )}
              {/* Reservation detail route */}
              <Route path="/reservation/:referenceNumber/detail" element={<ReservationDetailPage />} />
              {/* Restaurant manager route */}
              <Route
                path="/restaurant/:id/manage"
                element={<RestaurantManager propertyId={resolvedPropertyId} />}
              />
              {/* Spa manager route */}
              <Route path="/spa/:id/manage" element={<SpaManager propertyId={resolvedPropertyId} />} />
              {allowedTabs.includes('tourbooking') && (
                <Route path="/tourbooking" element={<TourBookingCenter />} />
              )}
              {allowedTabs.includes('settings') && (
                <Route path="/settings" element={<SettingsPage />} />
              )}
              <Route path="/hotelInfo/explore/:id" element={<ExplorePreviewPage />} />
              {allowedTabs.includes('guest') && (
                <Route path="/guest" element={<GuestCenter />} />
              )}
              {allowedTabs.includes('concierge') && (
                <Route path="/inbox" element={<ConciergeCenter />} />
              )}
            </Routes>
          </main>
          <ToastContainer position="top-center" autoClose={3000} />
        </>
      )}
    </div>
  );
}

export default App;
